/**
 * GameListSortHeader · Stacked 视图的 Game List 列头
 *
 * 单个统一 dropdown trigger（参考 figma 3277-19886 红框）：
 * - button render：横向并排所有游戏（cover icon + 名称），当前排序的游戏后面加 asc/desc icon；
 *   列尾 sort-icon 提示这是个下拉触发器
 * - dropdown panel：
 *   - 顶部 3 个方向选项（Asc / Des / Default Sorting，与 HoverSortCommonSimple 一致）
 *   - 分隔线
 *   - 游戏列表：[排序状态 icon] [cover] {game_name}
 *     - 当前排序游戏 → arrow-big-up / arrow-big-down
 *     - 其它游戏 → sort-icon
 *
 * 点击行为：
 * - 未选中游戏 → desc
 * - 同游戏 desc → asc
 * - 同游戏 asc → default（清空排序）
 * - 顶部方向选项 → 改变当前选中游戏的方向 / 清空
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { Dropdown, List } from 'tea-component';
import SvgIcon from '@/pages/home/components/SvgIcon';
import RenderImg from '@op/components/RenderImg';
import { DefaultImg } from '@op/constants/defaultImg';
import { TGameMap } from '../../hooks/useGameMap';
import { ICountrySortByState } from '../../hooks/useCountryCompare';
import './index.scss';
import useIntls from '@opm/hooks/useIntls';

/** SortType + 图标映射（与 HoverSortCommonSimple 一致） */
type TSortType = 'asc' | 'desc' | 'sort';
const sortIconMap: Record<TSortType, string> = {
  sort: 'sort-icon',
  asc: 'arrow-big-up',
  desc: 'arrow-big-down',
};

export interface IGameListSortHeaderProps {
  gameOrder: string[];
  gameMap: TGameMap;
  sortBy: ICountrySortByState;
  setSortBy: (s: ICountrySortByState) => void;
  /** 颜色映射（与 stacked bar 颜色对应） */
  gameColor?: Record<string, string>;
}

export default function GameListSortHeader({
  gameOrder,
  gameMap,
  sortBy,
  setSortBy,
  gameColor = {},
}: IGameListSortHeaderProps) {
  const { intl } = useIntls();
  /** 当前选中的游戏 + 方向（适配 SortType） */
  const currentOrder: TSortType =
    sortBy.by && sortBy.order
      ? (sortBy.order as TSortType)
      : 'sort';
  const activeGameId = sortBy.by;

  /**
   * 点击游戏行：
   * - 未选中 → desc
   * - 已选中 desc → asc
   * - 已选中 asc → default
   */
  const handleGameClick = (gameId: string) => {
    if (activeGameId !== gameId) {
      setSortBy({ by: gameId, order: 'desc' });
      return;
    }
    if (sortBy.order === 'desc') {
      setSortBy({ by: gameId, order: 'asc' });
      return;
    }
    // asc → default（清空）
    setSortBy({ order: "desc", by: "" });
  };

  /** Dropdown 内容 */
  const menu = (
    <List type="option" className="dbom-game-list-sort__menu">
      {/* 游戏列表 */}
      {gameOrder.map((gameId) => {
        const game = gameMap[gameId];
        const isCurrent = activeGameId === gameId;
        const iconType = isCurrent
          ? sortIconMap[currentOrder === 'sort' ? 'desc' : currentOrder]
          : sortIconMap.sort;
        return (
          <List.Item
            key={`game-${gameId}`}
            selected={isCurrent}
            onClick={() => handleGameClick(gameId)}
          >
            <SvgIcon type={iconType} className="text-icon" />
            <div className="dbom-game-list-sort__menu-item">
              <RenderImg
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  marginRight: 6,
                  flexShrink: 0,
                }}
                coverImage={game?.cover || ''}
                defaultImage={DefaultImg.GAME}
                mode="image"
              />
              <span className="dbom-game-list-sort__game-name">
                {game?.game_name || gameId}
              </span>
            </div>
          </List.Item>
        );
      })}
    </List>
  );

  const scrollerRef = useRef<HTMLSpanElement>(null);
  /**
   * 鼠标滚轮 → 转为横向滚动；同时阻止页面滚动
   * ⚠️ React 的 onWheel 是 passive listener，e.preventDefault() 不生效；
   *    必须用原生 addEventListener({ passive: false }) 才能 preventDefault
   */
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // 仅当垂直方向滚动量更大时才转 → 不影响 trackpad 二维滚动
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
        // 阻止页面滚动：仅当本元素仍可横向滚动时
        const canScrollMore =
          (e.deltaY > 0 && el.scrollLeft < el.scrollWidth - el.clientWidth) ||
          (e.deltaY < 0 && el.scrollLeft > 0);
        if (canScrollMore) {
          e.preventDefault();
          el.scrollLeft += e.deltaY;
        }
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  /** Button render：横向并排所有游戏；icon 必须可见，外层 scroll 隐藏滚动条 */
  const buttonContent = useMemo(
    () => (
      <>
        <span
          ref={scrollerRef}
          className="dbom-game-list-sort__btn"
        >
          {gameOrder.map((gameId) => {
            const game = gameMap[gameId];
            const isCurrent = activeGameId === gameId;
            const color = gameColor[gameId];
            return (
              <span
                key={gameId}
                className={`dbom-game-list-sort__btn-item ${isCurrent ? 'dbom-game-list-sort__btn-item--active' : ''}`}
                title={game?.game_name || gameId}
              >
                {color && (
                  <span
                    className="dbom-game-list-sort__btn-dot"
                    style={{ backgroundColor: color }}
                  />
                )}
                <RenderImg
                  style={{ width: 18, height: 18, borderRadius: 4, flexShrink: 0 }}
                  coverImage={game?.cover || ''}
                  defaultImage={DefaultImg.GAME}
                  mode="image"
                />
                <span className="dbom-game-list-sort__btn-name">
                  {game?.game_name || gameId}
                </span>
                {isCurrent && (
                  <SvgIcon
                    type={sortIconMap[currentOrder === 'sort' ? 'desc' : currentOrder]}
                    className="dbom-game-list-sort__btn-arrow"
                  />
                )}
              </span>
            );
          })}
        </span>
        <SvgIcon
          type={'icon-arrow-down'}
          className="dbom-game-list-sort__btn-suffix"
        />
      </>
    ),
    [gameOrder, gameMap, activeGameId, currentOrder, gameColor],
  );

  return (
    <Dropdown
      boxClassName="dbom-game-list-sort__dropdown"
      className="dbom-game-list-sort"
      appearance="pure"
      trigger="click"
      placement="bottom-end"
      button={buttonContent}
    >
      <span className="dbom-game-list-sort__dropdown-label">
        {intl('Sort By')}
      </span>
      {menu}
    </Dropdown>
  );
}
